import { useOrgTab } from '../../api/orgs';
import type { OrgStockSync } from '../../api/types';
import { DataTable, type DataColumn } from '../../components/DataTable';
import { QueryView } from '../../components/QueryView';
import { Section } from '../../components/Section';
import { Timestamp } from '../../components/Timestamp';

type Snapshot = OrgStockSync['snapshots'][number];
type Reject = OrgStockSync['push_rejects'][number];
type Warning = OrgStockSync['stale_warnings'][number];
type Discrepancy = OrgStockSync['discrepancies'][number];

const dash = (value: string | number | null) => value ?? '—';

const SNAPSHOT_COLUMNS: DataColumn<Snapshot>[] = [
  {
    id: 'location',
    header: 'Location',
    cell: item => item.location_name ?? item.location_id,
  },
  {
    id: 'taken',
    header: 'Snapshot taken',
    cell: item => <Timestamp value={item.taken_at} />,
  },
];

const REJECT_COLUMNS: DataColumn<Reject>[] = [
  { id: 'sku', header: 'SKU', cell: item => item.sku },
  { id: 'channel', header: 'Channel', cell: item => item.channel },
  { id: 'location', header: 'Location', cell: item => dash(item.location_id) },
  { id: 'reason', header: 'Reason', cell: item => dash(item.reason) },
  {
    id: 'rejected',
    header: 'Rejected',
    cell: item => <Timestamp value={item.rejected_at} />,
  },
];

const WARNING_COLUMNS: DataColumn<Warning>[] = [
  { id: 'sku', header: 'SKU', cell: item => dash(item.sku) },
  { id: 'channel', header: 'Channel', cell: item => dash(item.channel) },
  { id: 'location', header: 'Location', cell: item => dash(item.location_id) },
  {
    id: 'since',
    header: 'Stale since',
    cell: item => <Timestamp value={item.since} />,
  },
];

const DISCREPANCY_COLUMNS: DataColumn<Discrepancy>[] = [
  { id: 'sku', header: 'SKU', cell: item => item.sku },
  { id: 'location', header: 'Location', cell: item => dash(item.location_id) },
  { id: 'expected', header: 'Expected', cell: item => dash(item.expected) },
  { id: 'actual', header: 'Actual', cell: item => dash(item.actual) },
  {
    id: 'detected',
    header: 'Detected',
    cell: item => <Timestamp value={item.detected_at} />,
  },
];

export function StockSyncTab({ orgId }: { orgId: string }) {
  return (
    <QueryView query={useOrgTab(orgId, 'stock-sync')}>
      {({ snapshots, push_rejects, stale_warnings, discrepancies }) => (
        <>
          <Section title="Latest snapshot per location">
            <DataTable
              label="Latest snapshot per location"
              columns={SNAPSHOT_COLUMNS}
              rows={snapshots}
              getKey={item => item.location_id}
              empty="No snapshots"
            />
          </Section>
          <Section title="Push rejects">
            <DataTable
              label="Push rejects"
              columns={REJECT_COLUMNS}
              rows={push_rejects}
              getKey={item => `${item.channel}/${item.location_id}/${item.sku}`}
              empty="No push rejects"
            />
          </Section>
          <Section title="Stale warnings">
            <DataTable
              label="Stale warnings"
              columns={WARNING_COLUMNS}
              rows={stale_warnings}
              getKey={item => `${item.channel}/${item.location_id}/${item.sku}`}
              empty="No stale warnings"
            />
          </Section>
          <Section title="Discrepancies">
            <DataTable
              label="Discrepancies"
              columns={DISCREPANCY_COLUMNS}
              rows={discrepancies}
              getKey={item => `${item.location_id}/${item.sku}`}
              empty="No discrepancies"
            />
          </Section>
        </>
      )}
    </QueryView>
  );
}
